import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';

import { useAttributeGroups, useCreateAttribute, useUpdateAttribute } from '../../hooks';
import { attributeHasOptions, ATTRIBUTE_TYPE_OPTIONS } from '../../model/options';
import type {
  AttributeDefinition,
  AttributeTranslationInput,
  AttributeType,
  AttributeValidationRules,
  CatalogLanguage,
  MeasurementFamily,
} from '../../model/types';
import { resolveLocalizedText } from '../../lib/localizedText';
import type { ToastSeverity } from '../../lib/useToast';
import { AttributeOptionsManager } from './AttributeOptionsManager';
import { AttributeValidationRulesEditor } from './AttributeValidationRulesEditor';
import { LocalizedFieldsEditor } from './LocalizedFieldsEditor';
import type { LocalizedFieldValue } from './LocalizedFieldsEditor';

interface AttributeFormDialogProps {
  open: boolean;
  /** The attribute being edited (full detail — the list endpoint already returns it), or `null` to create. */
  editing: AttributeDefinition | null;
  languages: CatalogLanguage[];
  measurementFamilies: MeasurementFamily[];
  canManage: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

const VALIDATION_FIELDS_BY_TYPE: Record<AttributeType, (keyof AttributeValidationRules)[]> = {
  TEXT: ['regex', 'maxLength', 'gtin'],
  NUMBER: ['minValue', 'maxValue', 'decimals'],
  MEASUREMENT: ['minValue', 'maxValue', 'decimals'],
  DATE: ['minDate', 'maxDate'],
  BOOLEAN: [],
  SELECT: [],
  MULTISELECT: [],
};

function buildValidationPayload(
  type: AttributeType,
  value: AttributeValidationRules,
): AttributeValidationRules | undefined {
  const fields = VALIDATION_FIELDS_BY_TYPE[type];
  if (fields.length === 0) return undefined;
  const rules: AttributeValidationRules = {};
  for (const field of fields) {
    const fieldValue = value[field];
    if (fieldValue !== undefined && fieldValue !== '' && fieldValue !== false) {
      (rules as Record<string, unknown>)[field] = fieldValue;
    }
  }
  return Object.keys(rules).length > 0 ? rules : undefined;
}

/**
 * Create / edit an attribute dictionary entry (user-story-catalog-004/013): basic identity, the
 * type-dependent validation rules, the label translations, and — in edit mode, for SELECT/
 * MULTISELECT — the options manager.
 */
export function AttributeFormDialog({
  open,
  editing,
  languages,
  measurementFamilies,
  canManage,
  onClose,
  onResult,
}: AttributeFormDialogProps) {
  const { t, i18n } = useTranslation('catalog');
  const create = useCreateAttribute();
  const update = useUpdateAttribute();
  const groupsQuery = useAttributeGroups({ enabled: open });
  const groups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);
  const isEdit = editing !== null;
  const pending = create.isPending || update.isPending;

  const [code, setCode] = useState('');
  const [type, setType] = useState<AttributeType>('TEXT');
  const [groupCode, setGroupCode] = useState('');
  const [localizable, setLocalizable] = useState(false);
  const [searchable, setSearchable] = useState(false);
  const [active, setActive] = useState(true);
  const [measurementFamilyCode, setMeasurementFamilyCode] = useState('');
  const [validation, setValidation] = useState<AttributeValidationRules>({});
  const [labelValues, setLabelValues] = useState<LocalizedFieldValue[]>([]);

  useEffect(() => {
    if (!open) return;
    setCode(editing?.code ?? '');
    setType(editing?.type ?? 'TEXT');
    setGroupCode(editing?.groupCode ?? '');
    setLocalizable(editing?.localizable ?? false);
    setSearchable(editing?.searchable ?? false);
    setActive(editing?.active ?? true);
    setMeasurementFamilyCode(editing?.measurementFamilyCode ?? '');
    setValidation(editing?.validation ?? {});
    setLabelValues(
      (editing?.translations ?? []).map((translation) => ({
        locale: translation.locale,
        text: translation.label,
      })),
    );
  }, [open, editing]);

  const defaultLanguage = useMemo(() => languages.find((l) => l.defaultLocale), [languages]);
  const defaultLabelFilled =
    !defaultLanguage ||
    labelValues.some((v) => v.locale === defaultLanguage.locale && v.text.trim() !== '');
  const valid =
    code.trim() !== '' && defaultLabelFilled && (type !== 'MEASUREMENT' || measurementFamilyCode !== '');

  const submit = () => {
    if (!valid) return;
    const translations: AttributeTranslationInput[] = labelValues.map((v) => ({
      locale: v.locale,
      label: v.text,
    }));
    const validationPayload = buildValidationPayload(type, validation);

    const handlers = {
      onSuccess: () => {
        onResult('success', isEdit ? t('attributeFormDialog.saved') : t('attributeFormDialog.created'));
        onClose();
      },
      onError: (error: NormalizedApiError) => {
        if (error.code === 'SEARCHABLE_LIMIT_EXCEEDED') {
          onResult('error', t('attributeFormDialog.errors.searchableLimitExceeded'));
          return;
        }
        if (error.code === 'ATTRIBUTE_VALIDATION_FAILED') {
          onResult('error', t('attributeFormDialog.errors.validationFailed'));
          return;
        }
        onResult('error', error.message);
      },
    };

    if (isEdit && editing) {
      update.mutate(
        {
          code: editing.code,
          body: {
            measurementFamilyCode: type === 'MEASUREMENT' ? measurementFamilyCode : undefined,
            // '' is a deliberate, explicit "detach from the current group" — not "leave unchanged"
            // (omitting the field entirely would mean that instead).
            groupCode,
            localizable,
            active,
            searchable,
            validation: validationPayload,
            translations,
          },
        },
        handlers,
      );
      return;
    }

    create.mutate(
      {
        code: code.trim(),
        type,
        measurementFamilyCode: type === 'MEASUREMENT' ? measurementFamilyCode : undefined,
        groupCode,
        localizable,
        searchable,
        validation: validationPayload,
        translations,
      },
      handlers,
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? t('attributeFormDialog.editTitle') : t('attributeFormDialog.newTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('attributeFormDialog.codeLabel')}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={code.trim() === ''}
              disabled={isEdit || !canManage}
              autoFocus={!isEdit}
              fullWidth
              slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
            />
            <TextField
              select
              label={t('attributeFormDialog.typeLabel')}
              value={type}
              onChange={(e) => setType(e.target.value as AttributeType)}
              disabled={isEdit || !canManage}
              fullWidth
            >
              {ATTRIBUTE_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            select
            label={t('attributeFormDialog.groupLabel')}
            value={groupCode}
            onChange={(e) => setGroupCode(e.target.value)}
            disabled={!canManage}
            fullWidth
          >
            <MenuItem value="">
              <em>{t('attributeFormDialog.groupNoneOption')}</em>
            </MenuItem>
            {groups.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {resolveLocalizedText(
                  option.translations,
                  'label',
                  i18n.language,
                  option.code,
                  defaultLanguage?.locale,
                )}
              </MenuItem>
            ))}
          </TextField>

          {type === 'MEASUREMENT' ? (
            <TextField
              select
              label={t('attributeFormDialog.measurementFamilyLabel')}
              value={measurementFamilyCode}
              onChange={(e) => setMeasurementFamilyCode(e.target.value)}
              error={measurementFamilyCode === ''}
              disabled={!canManage}
              fullWidth
            >
              {measurementFamilies.map((family) => (
                <MenuItem key={family.code} value={family.code}>
                  {family.code}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          <Stack direction="row" spacing={3} flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  checked={localizable}
                  onChange={(e) => setLocalizable(e.target.checked)}
                  disabled={!canManage}
                />
              }
              label={t('attributeFormDialog.localizableLabel')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={searchable}
                  onChange={(e) => setSearchable(e.target.checked)}
                  disabled={!canManage}
                />
              }
              label={t('attributeFormDialog.searchableLabel')}
            />
            {isEdit ? (
              <FormControlLabel
                control={
                  <Switch checked={active} onChange={(e) => setActive(e.target.checked)} disabled={!canManage} />
                }
                label={t('attributeFormDialog.activeLabel')}
              />
            ) : null}
          </Stack>

          <Divider />

          <AttributeValidationRulesEditor
            type={type}
            value={validation}
            onChange={setValidation}
            disabled={!canManage}
          />

          <Divider />

          <LocalizedFieldsEditor
            languages={languages}
            values={labelValues}
            onChange={setLabelValues}
            fieldLabel={t('attributeFormDialog.labelFieldLabel')}
            disabled={!canManage}
          />

          {attributeHasOptions(type) ? (
            <>
              <Divider />
              {isEdit && editing ? (
                <AttributeOptionsManager
                  attributeCode={editing.code}
                  options={editing.options ?? []}
                  languages={languages}
                  canManage={canManage}
                  onNotify={onResult}
                />
              ) : (
                <Alert severity="info">{t('attributeFormDialog.optionsAfterCreateHint')}</Alert>
              )}
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || pending || !canManage}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
